import { useEffect, useState } from 'react';
import { Brain, Lightbulb, Quote, type LucideIcon } from 'lucide-react';
import { cn, isAutomated } from '@ai-tutor/ui';
import {
  WAIT_CARDS,
  WAIT_CARD_LABEL,
  firstCardIndex,
  type WaitCard,
  type WaitCardKind,
} from './waitCardDeck';

/** How long each card stays up before the next one (matches `animate-card-timer`). */
export const WAIT_CARD_MS = 30000;

const KIND_ICON: Record<WaitCardKind, LucideIcon> = {
  tip: Lightbulb,
  science: Brain,
  quote: Quote,
};

interface BuildWaitCardsProps {
  /** Seeds the first card, so it is stable for a lesson across re-renders. */
  lessonId?: string;
  /** One row under other content: a shorter card, smaller controls. */
  compact?: boolean;
  className?: string;
}

/**
 * Something worth reading while the lesson is built: app tips, study science
 * and a few well-sourced quotes, one card at a time - nothing to press.
 *
 * A card stays up for 30 s, shown by a hairline along its bottom that fills,
 * then the next fades in. It holds still while the pointer is over it, so a
 * long one can be finished. The card is not a live region: it changes on its
 * own every few seconds and would interrupt a screen reader each time; it is
 * read when reached. Under reduced motion there is no fade and no hairline;
 * under automation the deck holds its seeded first card for Storybook /
 * Chromatic.
 */
export function BuildWaitCards({
  lessonId,
  compact = false,
  className,
}: BuildWaitCardsProps) {
  const { card, autoplay, holding, hold } = useWaitDeck(lessonId);

  if (compact) {
    return (
      <section
        aria-label='While you wait'
        className={cn(
          'w-full border-t border-border pt-4 text-left',
          className
        )}
        {...hold}
      >
        {/* Keyed per card (and the timer apart from it): a new card replaces
            the old one and restarts the timer, never stacks beside it. */}
        <CompactCard key={card.id} card={card} />
        {autoplay && (
          <CardTimer
            key={`timer-${card.id}`}
            running={!holding}
            className='mt-3 rounded-full'
          />
        )}
      </section>
    );
  }

  return (
    <section
      aria-label='While you wait'
      className={cn(
        'relative w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-card text-left shadow-sm',
        className
      )}
      {...hold}
    >
      <div className='min-h-[10rem] px-7 py-7'>
        <FullCard key={card.id} card={card} />
      </div>
      {autoplay && (
        <CardTimer
          key={`timer-${card.id}`}
          running={!holding}
          className='absolute inset-x-0 bottom-0'
        />
      )}
    </section>
  );
}

/** The deck's state: the card up, whether it rotates, and the hover handlers
 *  that hold it. */
function useWaitDeck(lessonId: string | undefined) {
  const count = WAIT_CARDS.length;
  const [index, setIndex] = useState(() => firstCardIndex(lessonId, count));
  const [holding, setHolding] = useState(false);
  const autoplay = !isAutomated();
  const rotating = autoplay && !holding;

  useEffect(() => {
    if (!rotating) return;
    const timer = window.setTimeout(
      () => setIndex(i => (i + 1) % count),
      WAIT_CARD_MS
    );
    return () => window.clearTimeout(timer);
  }, [rotating, index, count]);

  return {
    card: WAIT_CARDS[index],
    autoplay,
    holding,
    hold: {
      onMouseEnter: () => setHolding(true),
      onMouseLeave: () => setHolding(false),
    },
  };
}

/** The hairline that fills while a card is up; holds with the card. */
function CardTimer({
  running,
  className,
}: {
  running: boolean;
  className?: string;
}) {
  return (
    <div
      aria-hidden='true'
      className={cn(
        'h-0.5 overflow-hidden bg-border/70 motion-reduce:hidden',
        className
      )}
    >
      <div
        className='h-full w-full origin-left animate-card-timer bg-primary/50'
        style={{ animationPlayState: running ? 'running' : 'paused' }}
      />
    </div>
  );
}

function KindBadge({ kind, small }: { kind: WaitCardKind; small?: boolean }) {
  const Icon = KIND_ICON[kind];
  return (
    <span
      aria-hidden='true'
      className={cn(
        'flex shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary',
        small ? 'h-7 w-7' : 'h-10 w-10 rounded-xl'
      )}
    >
      <Icon className={small ? 'h-3.5 w-3.5' : 'h-4 w-4'} strokeWidth={2} />
    </span>
  );
}

function FullCard({ card }: { card: WaitCard }) {
  return (
    <div
      data-wait-card={card.kind}
      className='animate-fade-in motion-reduce:animate-none'
    >
      <div className='flex items-center gap-3'>
        <KindBadge kind={card.kind} />
        <p className='font-mono text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground'>
          {WAIT_CARD_LABEL[card.kind]}
        </p>
      </div>
      {card.kind === 'quote' ? (
        <figure className='mt-5'>
          <blockquote className='text-pretty text-lg font-medium leading-relaxed text-foreground'>
            {card.body}
          </blockquote>
          <figcaption className='mt-3 text-sm text-muted-foreground'>
            <span className='text-foreground'>{card.author}</span>
            {card.source && <span> · {card.source}</span>}
          </figcaption>
        </figure>
      ) : (
        <p className='mt-4 text-pretty text-lg leading-relaxed text-foreground'>
          {card.body}
        </p>
      )}
    </div>
  );
}

function CompactCard({ card }: { card: WaitCard }) {
  return (
    <div
      data-wait-card={card.kind}
      className='flex min-w-0 animate-fade-in items-center gap-3 motion-reduce:animate-none'
    >
      <KindBadge kind={card.kind} small />
      <div className='min-w-0'>
        <p className='font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground'>
          {WAIT_CARD_LABEL[card.kind]}
        </p>
        <p className='line-clamp-2 text-sm text-foreground'>
          {card.kind === 'quote'
            ? `“${card.body}” — ${card.author}`
            : card.body}
        </p>
      </div>
    </div>
  );
}
